import * as yup from "yup";
import type { Unit } from "types/landlord/address";

export const SMOKING_POLICY_LABEL = "Smoking policy";

export const SMOKING_POLICY_MAX_LENGTH = 255;

export type ListField = "lease_terms" | "included_utilities";

type SyncedField = ListField | "smoking_policy";

export interface MarketingInfoFormValues {
  lease_terms: string;
  included_utilities: string;
  smoking_policy: string;
  pms_overrides: Partial<Record<SyncedField, boolean>>;
}

const toList = (raw: string): string[] => [
  ...new Set(
    raw
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)
  )
];

const smokingPolicySynced = (
  values: Pick<MarketingInfoFormValues, "pms_overrides">,
  isPmsUnit: boolean
): boolean => isPmsUnit && (values.pms_overrides?.smoking_policy ?? true);

// The smoking policy is only sent when entered manually, so only then does the
// API's length cap apply. A synced PMS value can be longer, and switching it to
// manual keeps that text, so flag it here rather than failing the save.
export const buildMarketingInfoSchema = (isPmsUnit: boolean) =>
  yup.object({
    smoking_policy: yup
      .string()
      .test(
        "smoking-policy-max-length",
        `${SMOKING_POLICY_LABEL} must be at most ${SMOKING_POLICY_MAX_LENGTH} characters`,
        function validate(value) {
          const parent = this.parent as MarketingInfoFormValues;
          return (
            smokingPolicySynced(parent, isPmsUnit) ||
            (value ?? "").trim().length <= SMOKING_POLICY_MAX_LENGTH
          );
        }
      )
  });

// A synced smoking policy is owned by the PMS, so it is left out of the PATCH;
// a blank manual value is sent as null to clear it.
export const buildMarketingInfoPayload = (
  values: MarketingInfoFormValues,
  unit: Pick<Unit, "pms_overrides">,
  isPmsUnit: boolean
) => ({
  lease_terms: toList(values.lease_terms),
  included_utilities: toList(values.included_utilities),
  ...(smokingPolicySynced(values, isPmsUnit)
    ? {}
    : { smoking_policy: values.smoking_policy.trim() || null }),
  ...(isPmsUnit && {
    pms_overrides: {
      ...unit.pms_overrides,
      ...values.pms_overrides
    }
  })
});
